import { DecorationSet } from "@tiptap/pm/view";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import BaseMathematics, {
    defaultShouldRender,
    MathematicsPlugin as BaseMathematicsPlugin,
    MathematicsOptions as BaseMathematicsOptions,
} from "@tiptap-pro/extension-mathematics";
import { insertMathExpression } from "./utils";
import { renderMathfieldDialog, openMathfieldDialog } from "./components/MathfieldDialog";
import { createRoot } from "react-dom/client";

export const MATHEMATICS_REGEX = /\$\$(.*?)\$\$/gi;

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        mathematics: {
            /** insert a content to editor but in katex */
            insertKatex: (props: { expression: string }) => ReturnType;
            openMathfieldDialog: () => ReturnType;
            /** if selection active, toggle selected text into katex otherwise open mathfield dialog */
            toggleMathematics: () => ReturnType;
        };
    }
}

export const Mathematics = BaseMathematics.extend<
    BaseMathematicsOptions,
    {
        mathematicsStorage: {
            /** position of latex indexes (current cursor, start & end) when clicked */
            nodeDetails?: { pos: number; from: number; to: number };
            currMathfieldId: string;
        };
    }
>({
    addStorage() {
        return {
            mathematicsStorage: {
                nodeDetails: undefined,
                currMathfieldId: `editor-${Math.random().toString(36).slice(2)}`,
            },
        };
    },

    addProseMirrorPlugins() {
        const getEditor = () => this.editor;
        const getOptions = () => this.options;
        const getStorage = () => this.storage.mathematicsStorage;

        const instantiateDialog = () => {
            const editor = getEditor();

            const editorEl = editor.view.nodeDOM(0)?.parentElement?.parentElement;
            if (!editorEl) return;

            const MATHLIVE_OUTER_CONTAINER_ID = "mathlive-outer-container";

            const el = document.getElementById(MATHLIVE_OUTER_CONTAINER_ID);
            if (el) return;

            const div = document.createElement("div");
            div.id = MATHLIVE_OUTER_CONTAINER_ID;

            editorEl.append(div);
            createRoot(div).render(
                renderMathfieldDialog({
                    mathfieldId: getStorage().currMathfieldId,
                    onInsert: ({ expression }) => {
                        const editor = getEditor();
                        editor.commands.insertKatex({ expression });
                    },
                })
            );
        };

        return [
            new Plugin({
                key: new PluginKey("mathematics-mathfield-dialog"),
                state: {
                    init: () => {
                        return DecorationSet.empty;
                    },
                    apply: () => DecorationSet.empty,
                },
                view() {
                    // give some time for editor to initialize all it's states before mounting the math editor popup
                    setTimeout(() => instantiateDialog(), 1000);

                    return {
                        destroy() {
                            const el = document.getElementById(getStorage().currMathfieldId);
                            if (el) el.remove();
                        },
                    };
                },
            }),

            // plugin to control if the katex renderer is clicked
            new Plugin({
                key: new PluginKey("mathematics-click-handler"),
                props: {
                    handleClick(view, _pos, event) {
                        const editor = getEditor();
                        const storage = getStorage();

                        let katexRenderer: HTMLElement | null = null;

                        // there could be two cases:
                        // 1. the `target` could directly the katex renderer if the node contain only one katex without any text/marks
                        // wraps it. ex: `$$\frac{1}{2}$$`
                        // 2. the `target` is a normal paragraph node that contains a katex renderer. This happened if
                        // katex in the node is wrapped by text or marks. ex: `Math equation $$\frac{1}{2}$$ of fraction`
                        const target = event.target as HTMLElement | null;

                        if (target?.className.includes("Tiptap-mathematics-editor")) {
                            katexRenderer = target;
                        } else if (target?.children[0]?.className.includes("Tiptap-mathematics-editor")) {
                            katexRenderer = target.children[0] as HTMLElement;
                        }

                        if (katexRenderer) {
                            const pos = view.posAtDOM(katexRenderer, 0);
                            const resolvedPos = view.state.doc.resolve(pos);
                            const node = resolvedPos.nodeAfter;
                            if (node) {
                                const startPos = pos;
                                const endPos = pos + katexRenderer.innerHTML.length;

                                event.preventDefault();

                                storage.nodeDetails = { from: startPos, to: endPos, pos: startPos };
                                const content = katexRenderer.innerText;

                                try {
                                    openMathfieldDialog({
                                        selectedLatex: content,
                                        mathfieldId: storage.currMathfieldId,
                                    });
                                } catch {
                                    editor.commands.insertKatex({ expression: content });
                                }
                            }
                        }
                    },
                },
            }),

            BaseMathematicsPlugin({ ...getOptions(), editor: this.editor }),
        ];
    },

    addCommands() {
        const getStorage = () => this.storage.mathematicsStorage;

        const resetStorage = () => {
            getStorage().nodeDetails = undefined;
        };

        return {
            insertKatex:
                ({ expression }: { expression: string }) =>
                ({ editor, state, chain }) => {
                    const { nodeDetails } = getStorage();

                    // if there is no node details, then use the current cursor position
                    const pos = {
                        start: nodeDetails?.from ?? state.selection.from,
                        end: nodeDetails?.to ?? state.selection.to,
                    };

                    insertMathExpression({ editor, chain, pos, expression });
                    resetStorage();

                    return true;
                },
            openMathfieldDialog: () => () => {
                const { currMathfieldId } = getStorage();
                openMathfieldDialog({ mathfieldId: currMathfieldId, selectedLatex: "" });
                return true;
            },
            toggleMathematics:
                () =>
                ({ editor, state, chain }) => {
                    if (state.selection.empty) {
                        editor.commands.openMathfieldDialog();
                        return true;
                    }

                    const expression = state.doc.textBetween(state.selection.from, state.selection.to);
                    const pos = { start: state.selection.from, end: state.selection.to };
                    insertMathExpression({ editor, chain, pos, expression });
                    return true;
                },
        };
    },

    addOptions() {
        return {
            ...this.parent,
            regex: MATHEMATICS_REGEX,
            shouldRender: (state, pos, _node) => defaultShouldRender(state, pos),
        };
    },
});
